import React from 'react';
import { TrendingUp, TrendingDown, Target, AlertTriangle } from 'lucide-react';
import { StartSitImpactPanelProps } from '../../types';

/**
 * StartSitImpactPanel - Analysis of start/sit decisions for the week
 * Features:
 * - Week result display (W/L, scores)
 * - Start/sit impact calculation, called out loudly when points were benched
 * - Top missed opportunities (bench > starter swaps)
 * - Visual indicators for positive/negative impact
 */
const StartSitImpactPanel: React.FC<StartSitImpactPanelProps> = ({
  analysis,
  matchup,
  onPlayerClick
}) => {
  const { week, team_score, optimal_score, start_sit_impact, missed_opportunities } = analysis;

  const getImpactColor = (impact: number) => {
    if (impact > 0) return 'text-accent-danger';
    if (impact < 0) return 'text-accent-primary';
    return 'text-text-secondary';
  };

  const getImpactIcon = (impact: number) => {
    if (impact > 0) return TrendingDown; // Negative impact (missed points)
    if (impact < 0) return TrendingUp; // Positive impact (optimal lineup)
    return Target; // Perfect lineup
  };

  const getResultColor = (result?: string) => {
    switch (result) {
      case 'win': return 'text-accent-primary';
      case 'loss': return 'text-accent-danger';
      default: return 'text-text-secondary';
    }
  };

  const formatResult = (result?: string) => {
    if (!result) return 'N/A';
    return result.toUpperCase();
  };

  // Roast the bench mistakes when they were big enough to matter
  const impactLabel = () => {
    if (start_sit_impact > 10) {
      const lostAnyway = matchup?.result === 'loss';
      return lostAnyway
        ? `Left ${start_sit_impact.toFixed(1)} pts on the bench. Could've won.`
        : `Left ${start_sit_impact.toFixed(1)} pts on the bench. Got away with it.`;
    }
    if (start_sit_impact > 0) return 'Points left on bench';
    if (start_sit_impact < 0) return 'Optimal lineup bonus';
    return 'Perfect lineup decisions';
  };

  const ImpactIcon = getImpactIcon(start_sit_impact);
  const bigMiss = start_sit_impact > 10;

  return (
    <div className="bg-background-secondary border border-fieldline rounded-lg p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
            <Target className="w-5 h-5 text-accent-info" />
            <span>Start/Sit Analysis</span>
          </h3>
          <div className="text-text-secondary text-sm">Week {week}</div>
        </div>

        {/* Week Result */}
        {matchup && (
          <div className="bg-background-primary/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-text-secondary text-sm">Week Result</span>
              <span className={`font-bold font-mono ${getResultColor(matchup.result)}`}>
                {formatResult(matchup.result)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-text-primary font-semibold font-mono">
                {team_score.toFixed(1)}
              </span>
              <span className="text-text-secondary">vs</span>
              <span className="text-text-primary font-semibold font-mono">
                {matchup.opponent_score.toFixed(1)}
              </span>
            </div>
            <div className="text-center text-text-secondary text-sm mt-1">
              vs {matchup.opponent_name}
            </div>
          </div>
        )}

        {/* Score Breakdown */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-background-primary/30 rounded-lg p-4 text-center">
            <div className="text-text-secondary text-sm mb-1">Actual Score</div>
            <div className="text-2xl font-bold font-mono text-text-primary">
              {team_score.toFixed(1)}
            </div>
          </div>
          <div className="bg-background-primary/30 rounded-lg p-4 text-center">
            <div className="text-text-secondary text-sm mb-1">Optimal Score</div>
            <div className="text-2xl font-bold font-mono text-text-primary">
              {optimal_score.toFixed(1)}
            </div>
          </div>
        </div>

        {/* Start/Sit Impact */}
        <div className={`rounded-lg p-4 border-l-4 ${
          start_sit_impact > 0
            ? bigMiss
              ? 'border-accent-danger bg-accent-danger/10'
              : 'border-accent-danger bg-background-primary/50'
            : start_sit_impact < 0
              ? 'border-accent-primary bg-background-primary/50'
              : 'border-fieldline bg-background-primary/50'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <ImpactIcon className={`w-6 h-6 ${getImpactColor(start_sit_impact)}`} />
              <div>
                <div className="text-text-primary font-semibold">
                  Start/Sit Impact
                </div>
                <div className={`text-sm ${bigMiss ? 'text-accent-danger italic' : 'text-text-secondary'}`}>
                  {impactLabel()}
                </div>
              </div>
            </div>
            <div className={`text-2xl font-bold font-mono ${getImpactColor(start_sit_impact)}`}>
              {start_sit_impact > 0 ? '+' : ''}{start_sit_impact.toFixed(1)}
            </div>
          </div>
        </div>

        {/* Missed Opportunities */}
        {missed_opportunities.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-accent-warning" />
              <h4 className="text-text-primary font-semibold">Missed Opportunities</h4>
            </div>

            <div className="space-y-2">
              {missed_opportunities.map((opportunity, index) => (
                <div key={index} className="bg-background-primary/30 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-text-secondary text-sm uppercase tracking-wide">
                      {opportunity.position_eligibility}
                    </div>
                    <div className="text-accent-danger font-bold font-mono">
                      -{opportunity.points_missed.toFixed(1)} pts
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-text-secondary">Started</div>
                      <div
                        className={`text-text-primary font-medium ${
                          opportunity.starter_player_id && onPlayerClick
                            ? 'cursor-pointer hover:text-accent-primary transition-colors'
                            : ''
                        }`}
                        onClick={() =>
                          opportunity.starter_player_id &&
                          onPlayerClick?.(opportunity.starter_player_id)
                        }
                      >
                        {opportunity.starter_name}
                      </div>
                      <div className="text-text-secondary font-mono">
                        {opportunity.starter_points.toFixed(1)} pts
                      </div>
                    </div>
                    <div>
                      <div className="text-text-secondary">Should have started</div>
                      <div
                        className={`text-text-primary font-medium ${
                          opportunity.bench_player_id && onPlayerClick
                            ? 'cursor-pointer hover:text-accent-primary transition-colors'
                            : ''
                        }`}
                        onClick={() =>
                          opportunity.bench_player_id &&
                          onPlayerClick?.(opportunity.bench_player_id)
                        }
                      >
                        {opportunity.bench_name}
                      </div>
                      <div className="text-accent-primary font-mono">
                        {opportunity.bench_points.toFixed(1)} pts
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Perfect Lineup Message */}
        {missed_opportunities.length === 0 && start_sit_impact === 0 && (
          <div className="bg-accent-primary/10 rounded-lg p-4 text-center">
            <TrendingUp className="w-8 h-8 text-accent-primary mx-auto mb-2" />
            <div className="text-accent-primary font-semibold">Perfect Lineup</div>
            <div className="text-text-secondary text-sm">
              You started your optimal lineup for Week {week}. The bench had nothing to say.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartSitImpactPanel;
